import React from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    Divider,
    Grid,
    LinearProgress,
    Paper,
    Stack,
    Step,
    StepLabel,
    Stepper,
    Typography,
} from '@mui/material';
import {
    Assessment,
    AutoAwesome,
    CheckCircle,
    Forum,
    HelpOutline,
    Memory,
    Psychology,
    WarningAmber,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const skills = [
    { id: 'S1', label: '画像生成代理人', icon: Psychology, color: '#0f766e' },
    { id: 'S2', label: '结构化记忆抽取', icon: Memory, color: '#2563eb' },
    { id: 'S3', label: '双代理模拟对话', icon: Forum, color: '#7c3aed' },
    { id: 'S4', label: '画像缺口追问', icon: HelpOutline, color: '#d97706' },
    { id: 'S5', label: '回答写入长期记忆', icon: CheckCircle, color: '#16a34a' },
    { id: 'S6', label: '兼容性报告输出', icon: Assessment, color: '#db2777' },
];

const simulation = [
    {
        speaker: 'Alice Agent',
        side: 'left',
        text: '我代表 Alice 先筛选长期关系匹配度。我们可以先比较双方对稳定关系和沟通方式的期待吗？',
    },
    {
        speaker: 'Ben Agent',
        side: 'right',
        text: '可以。Ben 重视长期稳定，也希望在分歧后冷静复盘。我还想确认日常沟通频率和未来规划。',
    },
    {
        speaker: 'Alice Agent',
        side: 'left',
        text: 'Alice 的表达更直接，也偏好提前规划。当前风险是不要在家庭边界和财务安排上提前假设。',
    },
    {
        speaker: 'Ben Agent',
        side: 'right',
        text: '同意。我们把不确定点标记成追问，让真实用户补充后再重新评估。',
    },
];

const memoryUpdates = [
    {
        topic: 'conflict_style',
        question: '如果争吵升级，你希望伴侣如何帮助你们回到建设性讨论？',
        answer: '先暂停半小时，再回来讨论具体问题。',
    },
    {
        topic: 'distance',
        question: '你能接受多长时间的异地关系，以及需要怎样的见面频率？',
        answer: '最多接受一年异地，并希望至少每月见面一次。',
    },
];

const reportReasons = [
    '双方已覆盖 4 个关键关系维度',
    '代理模拟能把不确定点转化为可追问问题',
    '报告同时给出匹配理由、风险和下一步建议',
];

const metricItems = [
    { label: '模拟完成', value: '100%' },
    { label: '记忆更新成功率', value: '100%' },
    { label: '报告字段完整率', value: '100%' },
];

const workflowSteps = [
    '创建代理',
    '抽取记忆',
    '模拟对话',
    '生成追问',
    '更新记忆',
    '输出报告',
];

const Wave2PrototypePage: React.FC = () => {
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = React.useState(0);

    const completedSteps = activeStep;
    const visibleSkills = skills.slice(0, Math.max(1, completedSteps));
    const visibleSimulation = completedSteps >= 3 ? simulation : simulation.slice(0, Math.max(0, completedSteps - 1));
    const visibleUpdates = completedSteps >= 5 ? memoryUpdates : [];
    const showGaps = completedSteps >= 4;
    const showReport = completedSteps >= 6;
    const reportScore = showReport ? 54 : completedSteps >= 5 ? 38 : 0;
    const progressValue = Math.round((completedSteps / workflowSteps.length) * 100);

    const runNextStep = () => {
        setActiveStep(step => Math.min(workflowSteps.length, step + 1));
    };

    const runFullDemo = () => {
        setActiveStep(workflowSteps.length);
    };

    const resetDemo = () => {
        setActiveStep(0);
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', color: '#0f172a' }}>
            <Box
                component="header"
                sx={{
                    borderBottom: '1px solid #e2e8f0',
                    bgcolor: 'rgba(255,255,255,0.92)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    backdropFilter: 'blur(12px)',
                }}
            >
                <Container maxWidth="xl">
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        justifyContent="space-between"
                        sx={{ py: 2 }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 2,
                                    display: 'grid',
                                    placeItems: 'center',
                                    bgcolor: '#0f172a',
                                    color: '#fff',
                                }}
                            >
                                <AutoAwesome fontSize="small" />
                            </Box>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                                    AI Matchmaker
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                    Wave 2 Prototype
                                </Typography>
                            </Box>
                        </Stack>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Button variant="outlined" color="inherit" onClick={() => navigate('/')}>
                                返回首页
                            </Button>
                            <Button variant="outlined" onClick={resetDemo}>
                                重置演示
                            </Button>
                            <Button variant="contained" onClick={runFullDemo}>
                                一键跑通
                            </Button>
                        </Stack>
                    </Stack>
                </Container>
            </Box>

            <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
                <Grid container spacing={3}>
                    <Grid item xs={12} lg={7}>
                        <Stack spacing={3}>
                            <Box>
                                <Chip
                                    label="复赛 | Wave 2 Prototype"
                                    size="small"
                                    sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 700, mb: 2 }}
                                />
                                <Typography
                                    component="h1"
                                    sx={{
                                        fontSize: { xs: '2rem', md: '3rem' },
                                        fontWeight: 800,
                                        lineHeight: 1.08,
                                        maxWidth: 760,
                                    }}
                                >
                                    学习型 AI 相亲代理人核心闭环演示
                                </Typography>
                                <Typography sx={{ color: '#475569', mt: 2, maxWidth: 760, fontSize: '1.05rem' }}>
                                    用一个可运行原型展示从用户画像、代理模拟、主动追问、记忆更新到兼容性报告的完整工作流。
                                </Typography>
                            </Box>

                            <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5, borderColor: '#e2e8f0' }}>
                                <Stack
                                    direction={{ xs: 'column', md: 'row' }}
                                    spacing={2}
                                    justifyContent="space-between"
                                    alignItems={{ xs: 'stretch', md: 'center' }}
                                >
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="h5" sx={{ fontWeight: 800 }}>
                                            可交互 Workflow Runner
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                                            当前进度：{progressValue}%。点击下一步可模拟评测环境依次执行关键 Skills。
                                        </Typography>
                                    </Box>
                                    <Stack direction="row" spacing={1}>
                                        <Button variant="outlined" onClick={resetDemo}>
                                            重置
                                        </Button>
                                        <Button
                                            variant="contained"
                                            onClick={runNextStep}
                                            disabled={completedSteps >= workflowSteps.length}
                                        >
                                            运行下一步
                                        </Button>
                                    </Stack>
                                </Stack>
                                <Box sx={{ mt: 2.5 }}>
                                    <Stepper activeStep={Math.min(activeStep, workflowSteps.length - 1)} alternativeLabel>
                                        {workflowSteps.map((label, index) => (
                                            <Step key={label} completed={completedSteps > index}>
                                                <StepLabel>{label}</StepLabel>
                                            </Step>
                                        ))}
                                    </Stepper>
                                </Box>
                            </Paper>

                            <Grid container spacing={2}>
                                {visibleSkills.map(skill => {
                                    const Icon = skill.icon;
                                    return (
                                        <Grid item xs={12} sm={6} md={4} key={skill.id}>
                                            <Paper
                                                variant="outlined"
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 2,
                                                    height: '100%',
                                                    borderColor: '#e2e8f0',
                                                }}
                                            >
                                                <Stack direction="row" spacing={1.5} alignItems="center">
                                                    <Box sx={{ color: skill.color, display: 'flex' }}>
                                                        <Icon />
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>
                                                            {skill.id}
                                                        </Typography>
                                                        <Typography sx={{ fontWeight: 700 }}>{skill.label}</Typography>
                                                    </Box>
                                                </Stack>
                                            </Paper>
                                        </Grid>
                                    );
                                })}
                            </Grid>

                            <Paper variant="outlined" sx={{ borderRadius: 2, p: 0, overflow: 'hidden', borderColor: '#e2e8f0' }}>
                                <Box sx={{ p: 2.5, bgcolor: '#fff' }}>
                                    <Typography variant="h5" sx={{ fontWeight: 800 }}>
                                        双代理模拟相亲
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                                        两个 AI 代理先代表用户做低成本筛选，并把不确定点沉淀为追问。
                                    </Typography>
                                </Box>
                                <Divider />
                                <Stack spacing={2} sx={{ p: 2.5, bgcolor: '#f8fafc' }}>
                                    {visibleSimulation.length === 0 && (
                                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                                            运行到“模拟对话”步骤后，这里会生成双代理对话记录。
                                        </Alert>
                                    )}
                                    {visibleSimulation.map((message, index) => (
                                        <Box
                                            key={`${message.speaker}-${index}`}
                                            sx={{
                                                alignSelf: message.side === 'left' ? 'flex-start' : 'flex-end',
                                                maxWidth: { xs: '100%', md: '78%' },
                                            }}
                                        >
                                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>
                                                {message.speaker}
                                            </Typography>
                                            <Paper
                                                sx={{
                                                    mt: 0.5,
                                                    p: 2,
                                                    borderRadius: 2,
                                                    bgcolor: message.side === 'left' ? '#ffffff' : '#eef2ff',
                                                    boxShadow: 'none',
                                                    border: '1px solid',
                                                    borderColor: message.side === 'left' ? '#e2e8f0' : '#c7d2fe',
                                                }}
                                            >
                                                <Typography>{message.text}</Typography>
                                            </Paper>
                                        </Box>
                                    ))}
                                </Stack>
                            </Paper>
                        </Stack>
                    </Grid>

                    <Grid item xs={12} lg={5}>
                        <Stack spacing={3}>
                            <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5, borderColor: '#e2e8f0' }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Assessment sx={{ color: '#2563eb' }} />
                                    <Typography variant="h5" sx={{ fontWeight: 800 }}>
                                        兼容性报告
                                    </Typography>
                                </Stack>
                                <Box sx={{ my: 3 }}>
                                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                                        <Typography sx={{ fontWeight: 700 }}>Overall score</Typography>
                                        <Typography sx={{ fontWeight: 800 }}>{reportScore} / 100</Typography>
                                    </Stack>
                                    <LinearProgress
                                        variant="determinate"
                                        value={reportScore}
                                        sx={{
                                            height: 10,
                                            borderRadius: 999,
                                            bgcolor: '#e2e8f0',
                                            '& .MuiLinearProgress-bar': { bgcolor: '#2563eb', borderRadius: 999 },
                                        }}
                                    />
                                </Box>
                                {!showReport ? (
                                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                                        运行到“输出报告”步骤后，这里会生成兼容性分数、理由、风险和下一步建议。
                                    </Alert>
                                ) : (
                                    <>
                                        <Stack spacing={1.5}>
                                            {reportReasons.map(reason => (
                                                <Stack direction="row" spacing={1.25} alignItems="flex-start" key={reason}>
                                                    <CheckCircle sx={{ color: '#16a34a', fontSize: 20, mt: 0.2 }} />
                                                    <Typography>{reason}</Typography>
                                                </Stack>
                                            ))}
                                        </Stack>
                                        <Alert
                                            icon={<WarningAmber />}
                                            severity="warning"
                                            sx={{ mt: 2.5, borderRadius: 2, alignItems: 'center' }}
                                        >
                                            仍有 3 个画像缺口，建议先回答追问再做最终判断。
                                        </Alert>
                                    </>
                                )}
                            </Paper>

                            <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5, borderColor: '#e2e8f0' }}>
                                <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
                                    追问与记忆更新
                                </Typography>
                                <Stack spacing={2}>
                                    {!showGaps && (
                                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                                            运行到“生成追问”步骤后，会显示画像缺口问题。
                                        </Alert>
                                    )}
                                    {showGaps && !visibleUpdates.length && (
                                        <Alert severity="warning" sx={{ borderRadius: 2 }}>
                                            已识别缺口：冲突处理、异地接受度、财务安排。继续运行可写入用户回答。
                                        </Alert>
                                    )}
                                    {visibleUpdates.map(update => (
                                        <Box key={update.topic}>
                                            <Chip
                                                label={update.topic}
                                                size="small"
                                                sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700, mb: 1 }}
                                            />
                                            <Typography sx={{ fontWeight: 700 }}>{update.question}</Typography>
                                            <Typography sx={{ color: '#475569', mt: 0.5 }}>
                                                写入记忆：{update.answer}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </Paper>

                            <Grid container spacing={2}>
                                {(showReport ? metricItems : metricItems.map(item => ({ ...item, value: '待生成' }))).map(metric => (
                                    <Grid item xs={12} sm={4} lg={12} key={metric.label}>
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                borderRadius: 2,
                                                p: 2,
                                                borderColor: '#e2e8f0',
                                                bgcolor: '#fff',
                                            }}
                                        >
                                            <Typography variant="body2" sx={{ color: '#64748b' }}>
                                                {metric.label}
                                            </Typography>
                                            <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5 }}>
                                                {metric.value}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </Stack>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default Wave2PrototypePage;
